// English. Must translate every key of tr.ts (checked by the compiler).

import type { MessageKey } from './tr';

export const en: Record<MessageKey, string> = {
  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.retry': 'Try again',
  'common.topics': '{n} topic|{n} topics',

  'topic.central': 'Central topic',
  'topic.main': 'Main topic',
  'topic.sub': 'Subtopic',
  'topic.default': 'Topic',
  'topic.floating': 'Floating topic',

  'layout.mindmap': 'Mind map',
  'layout.logic': 'Logic chart',
  'layout.org': 'Org chart',
  'layoutShort.mindmap': 'Mind',
  'layoutShort.logic': 'Logic',
  'layoutShort.org': 'Org',

  'shape.rounded': 'Rounded rectangle',
  'shape.pill': 'Pill',
  'shape.rect': 'Rectangle',
  'shape.underline': 'Underline',
  'shape.none': 'No shape',

  'branch.curve': 'Curved',
  'branch.elbow': 'Elbow',
  'branch.straight': 'Straight',

  'theme.fresh': 'Fresh',
  'theme.night': 'Night',
  'theme.paper': 'Paper',
  'theme.mono': 'Plain',

  'marker.priority-1': 'Priority 1',
  'marker.priority-2': 'Priority 2',
  'marker.priority-3': 'Priority 3',
  'marker.priority-4': 'Priority 4',
  'marker.priority-5': 'Priority 5',
  'marker.task-0': 'Not started',
  'marker.task-25': '25%',
  'marker.task-50': '50%',
  'marker.task-75': '75%',
  'marker.task-100': 'Done',
  'marker.flag-red': 'Red flag',
  'marker.flag-blue': 'Blue flag',
  'marker.flag-green': 'Green flag',
  'marker.symbol-star': 'Star',
  'marker.symbol-question': 'Question',
  'marker.symbol-idea': 'Idea',

  'cmd.palette': 'Command palette',
  'cmd.shortcuts': 'Keyboard shortcuts',
  'cmd.language': 'Interface language',
  'cmd.back': 'Back to maps',
  'cmd.addChild': 'Add subtopic',
  'cmd.addSibling': 'Add sibling topic',
  'cmd.addSiblingBefore': 'Add sibling before',
  'cmd.edit': 'Edit text',
  'cmd.remove': 'Delete',
  'cmd.undo': 'Undo',
  'cmd.redo': 'Redo',
  'cmd.copy': 'Copy',
  'cmd.cut': 'Cut',
  'cmd.paste': 'Paste',
  'cmd.collapse': 'Collapse / expand',
  'cmd.moveUp': 'Move topic up',
  'cmd.moveDown': 'Move topic down',
  'cmd.indent': 'Indent (under the topic above)',
  'cmd.outdent': 'Outdent (one level up)',
  'cmd.navLeft': 'Go left',
  'cmd.navRight': 'Go right',
  'cmd.navUp': 'Go up',
  'cmd.navDown': 'Go down',
  'cmd.relation': 'Add relation arrow',
  'cmd.floating': 'Add floating topic',
  'cmd.note': 'Add note',
  'cmd.save': 'Save to file',
  'cmd.saveAs': 'Save as',
  'cmd.toggleOutline': 'Map / outline view',
  'cmd.zoomIn': 'Zoom in',
  'cmd.zoomOut': 'Zoom out',
  'cmd.fit': 'Fit map',
  'cmd.centerSelected': 'Center selected topic',
  'cmd.history': 'Version history',
  'cmd.panel': 'Format panel',

  'key.space': 'Space',

  'editor.back': 'Back to maps',
  'editor.insert': 'Insert',
  'editor.stats': '{n} topic · {layout}|{n} topics · {layout}',
  'canvas.label': 'Mind map: {title}',
  'edit.topicText': 'Topic text',

  'tb.topic': 'Topic',
  'tb.subtopic': 'Subtopic',
  'tb.relation': 'Relation',
  'tb.relationTip': 'Relation arrow: connect to another topic',
  'tb.floating': 'Floating',
  'tb.floatingTip': 'Floating topic (double-click empty space)',
  'tb.marker': 'Marker',
  'tb.markerTip': 'Add a marker',
  'tb.note': 'Note',
  'tb.noteTip': 'Add a note',
  'tb.image': 'Image',
  'tb.imageTip': 'Add an image to the topic',
  'tb.removeImage': 'Remove image',
  'tb.removeImageTip': 'Remove the topic image',
  'tb.collapse': 'Collapse',
  'tb.delete': 'Delete',

  'view.label': 'View',
  'view.map': 'Map',
  'view.outline': 'Outline',

  'file.save': 'Save',
  'file.saveToComputer': 'Save to computer',
  'file.saveTip': 'Save the map as a file',
  'file.saveToTip': 'Save to {name}',
  'file.savedHere': 'Saved on this device',
  'file.savedTo': 'Saved · {name}',
  'file.saving': 'Saving…',
  'file.permission': 'Allow writing to {name}',
  'file.permissionTip': 'The browser asks again for permission to write the file',
  'file.writeFailed': 'Could not write {name}, try again',

  'export.menu': 'Export',
  'export.saveAs': 'Save as…',
  'export.unlink': 'Unlink file',
  'export.png': 'PNG image',
  'export.svg': 'SVG vector',
  'export.opml': 'OPML (outline)',
  'export.keizu': 'Keizu file (.keizu)',
  'export.json': 'Plain JSON (git friendly)',

  'toast.storageFull': 'Could not save: browser storage may be full.',
  'toast.savedTo': 'Saved to {name}',
  'toast.linked': 'Saved to {name}. Changes will be written to this file automatically.',
  'toast.downloaded': 'File downloaded',
  'toast.saveFailed': 'Could not save',
  'toast.unlinked': 'File unlinked; the map stays on this device',
  'toast.imageFailed': 'Could not add the image',
  'toast.cut': 'Topic cut',
  'toast.copied': 'Topic copied',
  'toast.pickRelation': 'Click the topic the relation goes to (Esc to cancel)',
  'toast.exportFailed': 'Export failed',
  'toast.restored': 'Version restored ({key} to undo)',

  'panel.label': 'Format',
  'panel.topic': 'Topic',
  'panel.map': 'Map',
  'panel.pickTopic': 'Select a topic to format it.',
  'panel.shape': 'Shape',
  'panel.fill': 'Fill',
  'panel.auto': 'Automatic',
  'panel.autoFill': 'Automatic fill',
  'panel.fillColor': 'Fill {color}',
  'panel.text': 'Text',
  'panel.fontSize': 'Font size',
  'panel.autoSize': 'Automatic size',
  'panel.bold': 'Bold',
  'panel.italic': 'Italic',
  'panel.branchLayout': 'Branch layout',
  'panel.inherit': 'Same',
  'panel.branchInherit': 'Same as the parent branch ({layout})',
  'panel.branchHint': "This topic's branches are arranged with the chosen layout; the rest of the map stays as it is.",
  'panel.markers': 'Markers',
  'panel.note': 'Note',
  'panel.notePlaceholderRoot': 'A note about the map…',
  'panel.notePlaceholder': 'Add a note to this topic…',
  'panel.theme': 'Theme',
  'panel.layout': 'Layout',
  'panel.branches': 'Branches',
  'panel.multicolor': 'Multicolor branches',
  'panel.relation': 'Relation',
  'panel.label2': 'Label',
  'panel.relationPlaceholder': 'e.g. feeds, depends on',
  'panel.deleteRelation': 'Delete relation',

  'outline.label': 'Outline: {title}',
  'outline.expand': 'Expand',
  'outline.collapse': 'Collapse',
  'note.untitled': 'Untitled topic',
  'note.dialog': 'Note for {topic}',
  'note.close': 'Close note',
  'note.closeTip': 'Close (Esc)',
  'note.empty': 'No note on this topic.',
  'note.showInTopic': 'Show inside the topic',
  'note.clear': 'Delete note',
  'note.presentLabel': 'Note',
  'outline.hasNote': 'Has a note',

  'palette.title': 'Command palette',
  'palette.placeholder': 'Search commands or topics…',
  'palette.commands': 'Commands',
  'palette.topics': 'Topics in this map',
  'palette.empty': 'No matching command or topic.',
  'palette.inNote': 'in note',
  'palette.hint': '↑↓ select · Enter run · Esc close',

  'keys.title': 'Keyboard shortcuts',
  'keys.intro': 'To change a shortcut, press "Add" and then the new key. Changes are kept on this device.',
  'keys.add': 'Add',
  'keys.press': 'Press a key… (Esc to cancel)',
  'keys.remove': 'Remove shortcut {key}',
  'keys.reset': 'Default',
  'keys.resetAll': 'Reset all to defaults',
  'keys.moved': '{key} now runs "{to}"; it was removed from "{from}".',
  'keys.outlineOnly': 'in outline',
  'keys.group.general': 'General',
  'keys.group.edit': 'Editing',
  'keys.group.navigate': 'Navigation',
  'keys.group.view': 'View',
  'keys.group.file': 'File',
  'keys.group.fixed': 'Fixed',
  'keys.fixed.newline': 'New line while editing',
  'keys.fixed.type': 'Start typing: edit the text',
  'keys.fixed.escape': 'Cancel selection or adding a relation',
  'keys.fixed.dragKey': 'Drag',
  'keys.fixed.drag': 'Move a topic under another one, or drop on empty space to make it float',
  'keys.fixed.dblKey': 'Double-click',
  'keys.fixed.dbl': 'Empty space: floating topic; topic: edit',
  'keys.fixed.wheel': 'wheel',
  'keys.fixed.zoom': 'Zoom in / out',

  'versions.title': 'Version history',
  'versions.intro': 'Keizu keeps earlier states of the map on this device by itself: when you open and close it, and at most every five minutes while you edit. Older versions are thinned out over time.',
  'versions.restore': 'Restore this version',
  'versions.empty': 'No saved versions yet. They build up here as you edit.',
  'versions.today': 'Today {time}',
  'versions.preview': 'Preview of {title}',
  'versions.reason.open': 'On open',
  'versions.reason.close': 'On close',
  'versions.reason.auto': 'Automatic',
  'versions.reason.restore': 'Before a restore',

  'time.justNow': 'just now',
  'time.minutesAgo': '{n} min ago',
  'time.today': 'today {time}',

  'home.newMap': 'New map',
  'home.openFile': 'Open file',
  'home.openFileTip': '.keizu, .json, .xmind, .opml or Markdown',
  'home.openHint': 'Opens .keizu, .xmind, OPML and Markdown. You can also drag a file onto the window.',
  'home.onDevice': 'On this device',
  'home.onDeviceDesktop': 'Your maps are kept on this computer. When you keep a map as a file with "Save", changes are written to that file too.',
  'home.onDeviceWeb': 'Your maps are kept in this browser; no account or server needed. For a backup or another device, take a file with "Save to computer".',
  'home.versionsNote': 'Recent versions of every map are kept automatically (version history).',
  'home.language': 'Language',
  'home.search': 'Search maps',
  'home.searchPlaceholder': 'Search all maps (titles, topics, notes)',
  'home.inTopics': 'Found in topics',
  'home.noTopicHits': 'Not found in any map’s topics.',
  'home.hitCount': '{n} match|{n} matches',
  'home.start': 'Start new',
  'home.myMaps': 'My maps',
  'home.titleMatches': 'Maps with a matching title',
  'home.noTitleMatch': 'No map title matches.',
  'home.empty': 'No maps yet. Create one above or open a file.',
  'home.fileTip': 'Also saved to this file',
  'home.deleteMap': 'Delete map {title}',
  'home.confirmDelete': 'Delete "{title}"? This cannot be undone.',
  'home.openFailed': 'Could not open the file.',
  'home.imported': '{n} maps imported.',
  'home.someUnreadable': 'Some files could not be read.',
  'home.dropHint': 'Drop to open',

  'versions.reason.sync': 'Before a sync',

  'home.sync': 'Cloud sync',
  'home.syncOff': 'If you like, connect your own server to sync your maps between devices.',

  'settings.title': 'Settings',
  'settings.back': 'Back to maps',

  'sync.title': 'Cloud sync',
  'sync.intro': 'Your maps are always saved on this device first. Once you connect a server they sync in the background, and you keep working when offline.',
  'sync.connectTitle': 'Connect your server',
  'sync.notConnected': 'Not connected yet',
  'sync.tab.address': 'Address',
  'sync.tab.code': 'Pairing code',
  'sync.tab.webdav': 'WebDAV',
  'sync.server': 'Server address',
  'sync.serverPlaceholder': 'notes.yourdomain.com',
  'sync.find': 'Find',
  'sync.finding': 'Looking…',
  'sync.found': 'Found {name} {version}',
  'sync.foundDetail': '{secure} · API v{api} · sign in with your account',
  'sync.https': 'Valid HTTPS',
  'sync.noHttps': 'No HTTPS: the connection is not encrypted',
  'sync.username': 'Username',
  'sync.password': 'Password',
  'sync.code': '6-digit code',
  'sync.codeHint': 'Press "Generate code" in the server panel, or use "Connect another device" on a device that is already connected.',
  'sync.connect': 'Connect',
  'sync.connecting': 'Connecting…',
  'sync.tokenNote': 'Your password is not stored; this device only gets its own token.',
  'sync.webdavUrl': 'WebDAV folder address',
  'sync.webdavPlaceholder': 'https://cloud.yourdomain.com/remote.php/dav/files/user/Keizu',
  'sync.webdavNote': 'Nextcloud, ownCloud and other WebDAV servers. Use an app password rather than your account password; it is stored on this device. In the browser the server must allow CORS; the desktop app does not need it.',
  'sync.connectedTo': 'Connected: {server}',
  'sync.signedInAs': 'as {user}',
  'sync.webdavAs': 'WebDAV · {user}',
  'sync.state.off': 'Not connected',
  'sync.state.idle': 'In sync',
  'sync.state.syncing': 'Syncing…',
  'sync.state.error': 'Some maps could not be synced',
  'sync.state.offline': 'Server unreachable; changes wait on this device',
  'sync.state.signedOut': 'Signed out; connect again',
  'sync.lastSync': 'Last sync: {when}',
  'sync.never': 'not yet',
  'sync.syncNow': 'Sync now',
  'sync.disconnect': 'Disconnect',
  'sync.reconnect': 'Connect again',
  'sync.confirmDisconnect': 'Disconnect this device from the server? Your maps stay on this device.',
  'sync.updateServer': 'The server is older than this app (server {version}). Update it: run "keizu-server update" on the server.',
  'sync.updateApp': 'This server needs a newer version of the Keizu app. Please update the app.',
  'sync.pairTitle': 'Connect another device',
  'sync.pairIntro': 'Scan the QR code on a phone, or enter this code on another computer.',
  'sync.pairMake': 'Generate code',
  'sync.pairExpires': 'Expires in {time}',
  'sync.pairExpired': 'Expired; generate a new code.',
  'sync.pairQr': 'Pairing QR code',
  'sync.noServerTitle': 'No server yet?',
  'sync.noServerText': 'Install it on Hetzner or any Linux server with one command. Domain and HTTPS are set up for you, and it also runs behind an nginx you already have.',
  'sync.guide': 'Open the setup guide',
  'sync.copy': 'Copy',
  'sync.copied': 'Copied',
  'sync.auto': 'Automatic sync',
  'sync.autoHint': 'Syncs a few seconds after each change, and every minute',
  'sync.policy': 'On a conflict',
  'sync.policyHint': 'When two devices edited the same map while offline',
  'sync.policy.merge': 'Merge topic by topic, else keep a copy',
  'sync.policy.copy': 'Always keep a copy',
  'sync.policy.server': "Keep the server's version",
  'sync.activity': 'Recent activity',
  'sync.activityEmpty': 'Nothing synced yet.',
  'sync.col.time': 'Time',
  'sync.col.map': 'Map',
  'sync.col.action': 'Action',
  'sync.action.pushed': 'Sent',
  'sync.action.pulled': 'Received',
  'sync.action.merged': 'Conflict · merged',
  'sync.action.copied': 'Conflict · copy kept',
  'sync.action.keptRemote': "Conflict · server's version kept",
  'sync.action.removedRemote': 'Deleted from the server',
  'sync.action.removedLocal': 'Deleted here (deleted on another device)',
  'sync.action.error': 'Error',
  'sync.copyTitle': '{title} (conflicting copy)',
  'sync.device.desktop': 'desktop',
  'sync.linkTitle': 'Connection link opened',
  'sync.linkText': 'Confirm the code to connect to {server}.',

  'layout.concept': "Concept map",
  'layout.timeline': "Timeline",
  'layout.fishbone': "Fishbone",
  'layout.matrix': "Matrix",
  'layoutShort.concept': "Concept",
  'layoutShort.timeline': "Timeline",
  'layoutShort.fishbone': "Fishbone",
  'layoutShort.matrix': "Matrix",
  'tpl.concept': "Concept",
  'tpl.conceptLink': "includes",
  'tpl.timeline': "Timeline",
  'tpl.event': "Event",
  'tpl.detail': "Detail",
  'tpl.fishbone': "Problem",
  'tpl.cause': "Cause",
  'tpl.subcause': "Sub-cause",
  'tpl.matrix': "Matrix",
  'tpl.column': "Column",
  'tpl.cell': "Cell",
  'panel.link': "Link",
  'panel.linkLabel': "Words on the line to the parent",
  'panel.linkLabelPlaceholder': "e.g. causes, includes",
  'panel.linkMap': "Link to another map",
  'panel.linkNone': "No link",
  'panel.linkMissing': "(Map not on this device)",
  'panel.linkUrl': "or to a web address",
  'panel.linkHint': "Click the link icon on the topic or press Alt+Enter to open it.",
  'toast.linkMissing': "The linked map is not on this device.",
  'toast.linkFailed': "Couldn't open the link.",
  'cmd.present': "Presentation mode",
  'cmd.openLink': "Open the topic's link",
  'cmd.share': "Share",
  'present.label': "Presentation",
  'present.prev': "Previous",
  'present.next': "Next",
  'present.exit': "Exit presentation (Esc)",
  'present.step': "{n} / {total}",

  'e2e.title': "End-to-end encryption",
  'e2e.on': "On",
  'e2e.off': "Off",
  'e2e.intro': "When on, maps are encrypted with your passphrase before they leave this device. The server (or WebDAV folder) stores them but can't read them.",
  'e2e.noShare': "Encrypted maps can't be shared or edited live, because the server can't see what changed. Maps others share with you are not affected.",
  'e2e.samePass': "Enter the same passphrase on every device that syncs.",
  'e2e.lost': "If you forget the passphrase, the encrypted copies on the server can't be opened; the maps on this device stay.",
  'e2e.setup': "Turn on encryption…",
  'e2e.pass': "Passphrase (at least 8 characters)",
  'e2e.pass2': "Type the passphrase again",
  'e2e.mismatch': "The passphrases don't match.",
  'e2e.tooShort': "The passphrase must be at least 8 characters.",
  'e2e.enable': "Encrypt and sync",
  'e2e.disable': "Turn off encryption",
  'e2e.confirmOff': "Turn off encryption? Maps are sent to the server readable on the next sync.",
  'home.sharedWithMe': "Shared with me",
  'home.sharedEdit': "{owner} · you can edit",
  'home.sharedView': "{owner} · view only",
  'home.sharedGone': "This map is no longer shared with you, or was deleted.",
  'live.keptServer': "Changes on this device couldn't be merged with the server's; the server's copy is open and this device's is in version history.",
  'live.peers': "{n} more people here",
  'live.readOnly': "You can only view this map.",
  'live.readOnlyBar': "View only",
  'live.sharedEdit': "Shared by {owner}",
  'live.sharedView': "Shared by {owner} · view only",
  'live.state.off': "Not connected",
  'live.state.connecting': "Connecting…",
  'live.state.live': "Live",
  'live.state.offline': "Connection lost, retrying",
  'live.state.closed': "Session ended",
  'share.button': "Share",
  'share.title': "Share map",
  'share.loading': "Loading…",
  'share.notSynced': "This map isn't on the server yet. It needs to sync before you can share it.",
  'share.syncNow': "Sync now",
  'share.encrypted': "End-to-end encryption is on. Encrypted maps can't be shared because the server can't see their content. You can turn it off in Settings → Cloud sync.",
  'share.intro': "Share with an account on this server. They see only this map, not your other maps. People editing at the same time see each other's changes live.",
  'share.user': "Username",
  'share.userPlaceholder': "Username",
  'share.role': "Access",
  'share.roleFor': "Access for {name}",
  'share.role.edit': "Can edit",
  'share.role.view': "Can view",
  'share.add': "Share",
  'share.remove': "Remove",
  'share.nobody': "Not shared with anyone yet.",
  'share.linkTitle': "Read-only link",
  'share.linkHint': "Anyone with the link can view the map, even without an account, but can't change it.",
  'share.linkCreate': "Create link",
  'share.linkOn': "Anyone who has the link can see the current map.",
  'share.linkOff': "Revoke link",
  'share.liveNow': "{n} people are in this map right now.",
  'view.readOnly': "Read only",
  'view.notFound': "This share link is invalid or was revoked.",
  'view.madeWith': "Shared with Keizu",
};
